/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { getEmbeddedResources, ResourceSearchService, StateResource } from '@common/tech-shared';
import { Resource } from '@ngxp/rest';
import { InstantSearchResult } from '@ods/system';
import { isNull } from 'lodash-es';
import { map, Observable } from 'rxjs';
import { ZustaendigeStelleService } from '../zustaendige-stelle.service';
import { OrganisationsEinheitListLinkRel } from './organisations-einheit.linkrel';
import { OrganisationsEinheitListResource, OrganisationsEinheitResource } from './organisations-einheit.model';
import { formatAnschrift } from './organisations-einheit.util';

export class OrganisationsEinheitService implements ZustaendigeStelleService<OrganisationsEinheitResource> {
  constructor(
    private readonly searchService: ResourceSearchService<
      Resource,
      OrganisationsEinheitListResource,
      OrganisationsEinheitResource
    >,
  ) {}

  public getSearchResultList(): Observable<InstantSearchResult<OrganisationsEinheitResource>[]> {
    return this.searchService
      .getResultList()
      .pipe(
        map((organisationsEinheitStateListResource: StateResource<OrganisationsEinheitListResource>) =>
          this.mapListResource(organisationsEinheitStateListResource),
        ),
      );
  }

  mapListResource(
    organisationsEinheitStateListResource: StateResource<OrganisationsEinheitListResource>,
  ): InstantSearchResult<OrganisationsEinheitResource>[] {
    return this.getOrganisationsEinheitResources(organisationsEinheitStateListResource).map(
      (organisationsEinheitResource: OrganisationsEinheitResource) => this.mapToInstantSearchResult(organisationsEinheitResource),
    );
  }

  private getOrganisationsEinheitResources(
    organisationsEinheitStateListResource: StateResource<OrganisationsEinheitListResource>,
  ): OrganisationsEinheitResource[] {
    const resources: OrganisationsEinheitResource[] = getEmbeddedResources(
      organisationsEinheitStateListResource,
      OrganisationsEinheitListLinkRel.ORGANISATION_EINHEIT_HEADER_LIST,
    );
    return isNull(resources) ? [] : resources;
  }

  mapToInstantSearchResult(zustaendigeStelle: OrganisationsEinheitResource): InstantSearchResult<OrganisationsEinheitResource> {
    return {
      title: zustaendigeStelle.name,
      description: formatAnschrift(zustaendigeStelle.anschrift),
      data: zustaendigeStelle,
    };
  }

  public search(searchBy: string): void {
    this.searchService.search(searchBy);
  }

  public clearSearchResult(): void {
    this.searchService.clearResultList();
  }

  public getSelectedResult(): Observable<OrganisationsEinheitResource> {
    return this.searchService.getSelectedResult();
  }

  public selectSearchResult(organisationsEinheitResource: OrganisationsEinheitResource): void {
    this.searchService.selectResult(organisationsEinheitResource);
  }

  public clearSelectedResult(): void {
    this.searchService.clearSelectedResult();
  }
}
